import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { RouteComponentProps } from "react-router-dom";

import PostAddIcon from "@mui/icons-material/PostAdd";
import Alert from "@mui/material/Alert";

import { captureHandledException } from "src/services/sentry";

import { getCompanyCourseUsage, AddonUsageResponse } from "../../api/addons";
import { QuotaExceededError } from "../../api/request";
import Button from "../../components/Button";
import TableComponent from "../../components/TableComponent";
import Spinner from "../../components/Spinner";
import {
  getMoodleCourses,
  getCompanyCoursesForCrew,
  updateMoodleCourse,
  updateCompanyCourse,
  archiveCompanyCourse,
  CourseResponse,
  CourseRequest,
} from "../../api/courses";
import MoodleCourseItem from "./MoodleCourseItem";

interface MatchParams {
  crewId?: string;
}

const MoodleCoursesTable: React.FC<RouteComponentProps<MatchParams>> = ({ match, history }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { crewId } = match.params;
  const isCompanyCourse = !!crewId;

  const [refresh, setRefresh] = useState(1);
  const [courses, setCourses] = useState<CourseResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addonUsage, setAddonUsage] = useState<AddonUsageResponse | null>(null);
  const isInitialLoad = useRef(true);

  function onEdit(id: number) {
    history.push(`${match.url}/edit/${id}`);
  }

  function onAdd() {
    history.push(`${match.url}/add`);
  }

  function triggerRefresh() {
    setRefresh((prev) => prev * -1);
  }

  async function onVisibleChange(id: number, newVisible: number) {
    await updateMoodleCourse(id, { visible: newVisible } as CourseRequest);
    triggerRefresh();
  }

  async function onCompanyVisibleChange(id: number, newVisible: number) {
    await updateCompanyCourse(Number(crewId!), String(id), { visible: newVisible });
    triggerRefresh();
  }

  async function onIsRefresherChange(id: number, newValue: boolean) {
    try {
      await updateCompanyCourse(Number(crewId!), String(id), { isRefresher: newValue });
      triggerRefresh();
    } catch (error) {
      if (error instanceof QuotaExceededError) {
        enqueueSnackbar(t("course.quota_exceeded"), { variant: "warning" });
      }
    }
  }

  async function onArchive(id: number) {
    await archiveCompanyCourse(Number(crewId!), String(id));
    triggerRefresh();
  }

  async function onShowChange(id: number, newShow: number) {
    await updateMoodleCourse(id, { showAtAllCourses: newShow } as CourseRequest);
    triggerRefresh();
  }

  useEffect(() => {
    (async () => {
      if (isInitialLoad.current) {
        setLoading(true);
      }
      setError(null);
      try {
        const [newCourses, usage] = await Promise.all([
          isCompanyCourse ? getCompanyCoursesForCrew(Number(crewId!)) : getMoodleCourses(),
          isCompanyCourse ? getCompanyCourseUsage(Number(crewId!)).catch(() => null) : Promise.resolve(null),
        ]);
        setCourses(newCourses);
        if (isCompanyCourse) setAddonUsage(usage);
      } catch (err) {
        captureHandledException(err, { feature: "moodleCoursesTable", action: "load", crewId });
        setError(t("course.load_failed"));
      } finally {
        setLoading(false);
        isInitialLoad.current = false;
      }
    })();
  }, [refresh, crewId, isCompanyCourse, t]);

  if (loading) {
    return <Spinner message={t("loading")} />;
  }

  if (error) {
    return (
      <>
        <h2>{isCompanyCourse ? t("compliance.all_company_lms_courses") : t("all_moodle_courses")}</h2>
        <Alert severity="error">{error}</Alert>
        <Button size="small" onClick={onAdd} style={{ marginTop: "1rem" }}>
          <PostAddIcon />
          {isCompanyCourse ? t("course.add_company_lms_action") : t("add_new_course")}
        </Button>
      </>
    );
  }

  const quotaCount = addonUsage?.used ?? 0;
  const maxCourses = addonUsage?.maxCourses ?? null;
  const isAtLimit = addonUsage?.remaining === 0;

  return (
    <>
      <h2>{isCompanyCourse ? t("compliance.all_company_lms_courses") : t("all_moodle_courses")}</h2>
      {isCompanyCourse && maxCourses !== null && (
        <Alert severity={isAtLimit ? "error" : "info"} sx={{ marginBottom: "1rem" }}>
          {isAtLimit
            ? t("course.quota_limit_reached", { used: quotaCount, max: maxCourses })
            : t("course.quota_usage", { used: quotaCount, max: maxCourses })}
        </Alert>
      )}
      <TableComponent
        header={
          isCompanyCourse
            ? [
                { title: t("table_headers.id"), sort: "id", style: { width: "5%" } },
                { title: t("table_headers.full_name"), sort: "displayName", style: { width: "18%" } },
                { title: t("table_headers.summary"), sort: "summary", style: { width: "18%" } },
                { title: t("table_headers.start_date"), sort: "startDate", style: { width: "10%" } },
                { title: t("table_headers.visible"), sort: "visible", style: { width: "7%" } },
                { title: t("table_headers.is_refresher"), sort: "isRefresher", style: { width: "8%" } },
                { title: t("table_headers.configured"), sort: "configured", style: { width: "10%" } },
                { title: t("table_headers.theme"), sort: "courseCategory", style: { width: "12%" } },
                { title: t("table_headers.action"), style: { width: "12%" } },
              ]
            : [
                { title: t("table_headers.id"), sort: "id", style: { width: "5%" } },
                { title: t("table_headers.full_name"), sort: "displayName", style: { width: "10%" } },
                { title: t("table_headers.summary"), sort: "summary", style: { width: "20%" } },
                { title: t("table_headers.start_date"), sort: "startDate", style: { width: "10%" } },
                { title: t("table_headers.visible"), sort: "visible", style: { width: "5%" } },
                {
                  title: t("table_headers.show_at_all_courses"),
                  sort: "showAtAllCourses",
                  style: { width: "5%" },
                },
                {
                  title: t("table_headers.refreshers"),
                  sort: "refreshers",
                  style: { width: "15%" },
                },
                {
                  title: t("table_headers.configured"),
                  sort: "configured",
                  style: { width: "10%" },
                },
                { title: t("table_headers.theme"), sort: "courseCategory", style: { width: "10%" } },
                { title: t("table_headers.action"), style: { width: "10%" } },
              ]
        }
        items={courses}
        renderChildren={(course) => (
          <MoodleCourseItem
            key={course.id}
            onEdit={onEdit}
            onVisibleChange={isCompanyCourse ? onCompanyVisibleChange : onVisibleChange}
            onShowChange={onShowChange}
            onIsRefresherChange={onIsRefresherChange}
            onArchive={onArchive}
            course={course}
            isCompanyCourse={isCompanyCourse}
          />
        )}
        search={["displayName", "id"]}
        filter={
          !isCompanyCourse
            ? {
                filterBy: "visible",
                controlLabel: t("show_inactive_courses"),
              }
            : undefined
        }
        TopComponent={
          <Button size="small" onClick={onAdd}>
            <PostAddIcon />
            {isCompanyCourse ? t("course.add_company_lms_action") : t("add_new_course")}
          </Button>
        }
        pagination
      />
    </>
  );
};

export default MoodleCoursesTable;
